
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Save, Plus, Trash2, X } from 'lucide-react';
import { useTeacherEdit, TeacherEditData, TeacherSubjectRow, TeacherStyleRow, TeacherCertificateRow } from '@/hooks/useTeacherEdit';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';

const TagInput: React.FC<{
  tags: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  allOptions?: string[];
  listId: string;
}> = ({ tags, onAdd, onRemove, allOptions, listId }) => {
  const [inputVal, setInputVal] = useState('');
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1">
            {tag}
            <X className="h-3 w-3 cursor-pointer" onClick={() => onRemove(tag)} />
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="พิมพ์เพื่อเพิ่ม..."
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(inputVal.trim()); setInputVal(''); } }}
          list={`${listId}-options`}
        />
        {allOptions && (
          <datalist id={`${listId}-options`}>
            {allOptions.filter(o => !tags.includes(o)).map(o => <option key={o} value={o} />)}
          </datalist>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => { onAdd(inputVal.trim()); setInputVal(''); }}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminTeacherEditPage: React.FC = () => {
  const { teacherCode } = useParams<{ teacherCode: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, filterOptions, save, isSaving } = useTeacherEdit(teacherCode!);

  const [formData, setFormData] = useState<TeacherEditData | null>(null);

  useEffect(() => {
    if (data) {
      setFormData(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !data || !formData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card><CardContent className="py-8 text-center text-destructive">
          {error ? `เกิดข้อผิดพลาด: ${(error as Error).message}` : 'ไม่พบข้อมูลครู'}
        </CardContent></Card>
      </div>
    );
  }

  const updateBasic = (field: string, value: any) => {
    setFormData(prev => prev ? { ...prev, basic: { ...prev.basic, [field]: value } } : prev);
  };

  const updateAdditional = (field: string, value: string) => {
    setFormData(prev => prev ? { ...prev, additional: { ...prev.additional, [field]: value } } : prev);
  };

  const handleSave = async () => {
    if (formData) await save(formData);
  };

  // Subject helpers
  const addSubject = () => {
    setFormData(prev => prev ? { ...prev, subjects: [...prev.subjects, { subject: '', education_stage: '', course_type: '' }] } : prev);
  };
  const removeSubject = (i: number) => {
    setFormData(prev => prev ? { ...prev, subjects: prev.subjects.filter((_, idx) => idx !== i) } : prev);
  };
  const updateSubject = (i: number, field: keyof TeacherSubjectRow, value: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const subjects = [...prev.subjects];
      subjects[i] = { ...subjects[i], [field]: value };
      return { ...prev, subjects };
    });
  };

  // Style helpers
  const addStyle = () => {
    setFormData(prev => prev ? { ...prev, teachingStyles: [...prev.teachingStyles, { teaching_style: '', level: 0 }] } : prev);
  };
  const removeStyle = (i: number) => {
    setFormData(prev => prev ? { ...prev, teachingStyles: prev.teachingStyles.filter((_, idx) => idx !== i) } : prev);
  };
  const updateStyle = (i: number, field: keyof TeacherStyleRow, value: any) => {
    setFormData(prev => {
      if (!prev) return prev;
      const styles = [...prev.teachingStyles];
      styles[i] = { ...styles[i], [field]: value };
      return { ...prev, teachingStyles: styles };
    });
  };

  // Certificate helpers
  const addCertificate = () => {
    setFormData(prev => prev ? { ...prev, certificates: [...prev.certificates, { certificate_id: '', grade: '', status: '', certificate_received: false, note: '' }] } : prev);
  };
  const removeCertificate = (i: number) => {
    setFormData(prev => prev ? { ...prev, certificates: prev.certificates.filter((_, idx) => idx !== i) } : prev);
  };
  const updateCertificate = (i: number, field: keyof TeacherCertificateRow, value: any) => {
    setFormData(prev => {
      if (!prev) return prev;
      const certs = [...prev.certificates];
      certs[i] = { ...certs[i], [field]: value };
      return { ...prev, certificates: certs };
    });
  };

  // Tag helpers for simple string arrays
  const addTag = (field: 'specialized' | 'branches' | 'studentProficiency', value: string) => {
    if (!value) return;
    setFormData(prev => {
      if (!prev || prev[field].includes(value)) return prev;
      return { ...prev, [field]: [...prev[field], value] };
    });
  };
  const removeTag = (field: 'specialized' | 'branches' | 'studentProficiency', value: string) => {
    setFormData(prev => prev ? { ...prev, [field]: prev[field].filter(v => v !== value) } : prev);
  };

  const renderSelectField = (label: string, value: string, options: string[], onChange: (v: string) => void) => (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Select value={value || '__none__'} onValueChange={v => onChange(v === '__none__' ? '' : v)}>
        <SelectTrigger><SelectValue placeholder="เลือก..." /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">-- ไม่ระบุ --</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const renderTextField = (label: string, value: string, onChange: (v: string) => void) => (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Input value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );

  const renderTagSection = (field: 'specialized' | 'branches' | 'studentProficiency', allOptions?: string[]) => (
    <TagInput
      tags={formData[field]}
      onAdd={(v) => addTag(field, v)}
      onRemove={(v) => removeTag(field, v)}
      allOptions={allOptions}
      listId={field}
    />
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto">
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/teachers/${teacherCode}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />กลับ
            </Button>
            <div>
              <h1 className="text-2xl font-bold">แก้ไขข้อมูลครู</h1>
              <p className="text-muted-foreground">{formData.basic.code} - {formData.basic.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            บันทึก
          </Button>
        </div>

        {/* Section A: Basic Info */}
        <Card>
          <CardHeader><CardTitle>ข้อมูลพื้นฐาน</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderTextField('ชื่อ', formData.basic.name, v => updateBasic('name', v))}
              {renderTextField('ชื่อเล่น', formData.basic.nickname, v => updateBasic('nickname', v))}
              {renderTextField('อีเมล', formData.basic.email, v => updateBasic('email', v))}
              {renderSelectField('Tier', formData.basic.tier, filterOptions?.tiers || [], v => updateBasic('tier', v))}
              {renderSelectField('เพศ', formData.basic.gender, filterOptions?.genders || [], v => updateBasic('gender', v))}
              {renderSelectField('บุคลิกภาพ', formData.basic.personality, filterOptions?.personalities || [], v => updateBasic('personality', v))}
              {renderTextField('มหาวิทยาลัย', formData.basic.university, v => updateBasic('university', v))}
              {renderTextField('สาขา', formData.basic.major, v => updateBasic('major', v))}
              {renderSelectField('สอน Trial', formData.basic.trial_lesson, filterOptions?.trialLessons || [], v => updateBasic('trial_lesson', v))}
              {renderSelectField('ประเภทการสอน', formData.basic.teaching_class, filterOptions?.teachingClasses || [], v => updateBasic('teaching_class', v))}
              {renderTextField('คอร์สที่ลงทะเบียน', formData.basic.registration_course, v => updateBasic('registration_course', v))}
              {renderTextField('สไตล์การสอน (Academic)', formData.basic.teaching_style_by_academic, v => updateBasic('teaching_style_by_academic', v))}
              {renderTextField('สไตล์การสอน (OP)', formData.basic.teaching_style_by_op, v => updateBasic('teaching_style_by_op', v))}
              {renderTextField('หมายเหตุสไตล์การสอน', formData.basic.remark_teaching_style, v => updateBasic('remark_teaching_style', v))}
              {renderTextField('หมายเหตุสาขา', formData.basic.remark_branch, v => updateBasic('remark_branch', v))}
              {renderTextField('เวลาที่ไม่สอน', formData.basic.time_not_teaching, v => updateBasic('time_not_teaching', v))}
              {renderSelectField('สถานะ', formData.basic.status, filterOptions?.statuses || [], v => updateBasic('status', v))}
              <div className="space-y-1.5 flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.basic.is_tax_pnd3}
                    onCheckedChange={v => updateBasic('is_tax_pnd3', !!v)}
                  />
                  <Label>ภาษี ภ.ง.ด.3</Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section B: Additional Info */}
        <Card>
          <CardHeader><CardTitle>ข้อมูลเพิ่มเติม</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderTextField('ธนาคาร', formData.additional.bank, v => updateAdditional('bank', v))}
              {renderTextField('เลขบัญชี', formData.additional.bank_account, v => updateAdditional('bank_account', v))}
              {renderTextField('ที่อยู่', formData.additional.address, v => updateAdditional('address', v))}
              {renderTextField('เลขประจำตัวผู้เสียภาษี', formData.additional.uid, v => updateAdditional('uid', v))}
            </div>
          </CardContent>
        </Card>

        {/* Section C: Subjects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>วิชาที่สอน</CardTitle>
            <Button variant="outline" size="sm" onClick={addSubject}><Plus className="h-4 w-4 mr-1" />เพิ่มวิชา</Button>
          </CardHeader>
          <CardContent>
            {formData.subjects.length === 0 ? (
              <p className="text-muted-foreground text-sm">ยังไม่มีวิชา</p>
            ) : (
              <div className="space-y-3">
                {formData.subjects.map((s, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">วิชา</Label>
                      <Input value={s.subject} onChange={e => updateSubject(i, 'subject', e.target.value)} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">ระดับชั้น</Label>
                      <Input value={s.education_stage} onChange={e => updateSubject(i, 'education_stage', e.target.value)} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">ประเภทคอร์ส</Label>
                      <Input value={s.course_type} onChange={e => updateSubject(i, 'course_type', e.target.value)} />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeSubject(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section D: Specialized Tutorings */}
        <Card>
          <CardHeader><CardTitle>การสอนเฉพาะทาง</CardTitle></CardHeader>
          <CardContent>{renderTagSection('specialized', (filterOptions?.allSpecialized || []) as string[])}</CardContent>
        </Card>

        {/* Section E: Teaching Styles */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>สไตล์การสอน</CardTitle>
            <Button variant="outline" size="sm" onClick={addStyle}><Plus className="h-4 w-4 mr-1" />เพิ่ม</Button>
          </CardHeader>
          <CardContent>
            {formData.teachingStyles.length === 0 ? (
              <p className="text-muted-foreground text-sm">ยังไม่มีข้อมูล</p>
            ) : (
              <div className="space-y-3">
                {formData.teachingStyles.map((s, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">สไตล์</Label>
                      <Input value={s.teaching_style} onChange={e => updateStyle(i, 'teaching_style', e.target.value)} />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">ระดับ</Label>
                      <Input type="number" value={s.level} onChange={e => updateStyle(i, 'level', parseInt(e.target.value) || 0)} />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeStyle(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section F: Branches */}
        <Card>
          <CardHeader><CardTitle>สาขา</CardTitle></CardHeader>
          <CardContent>{renderTagSection('branches')}</CardContent>
        </Card>

        {/* Section G: Certificates */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>ใบรับรอง</CardTitle>
            <Button variant="outline" size="sm" onClick={addCertificate}><Plus className="h-4 w-4 mr-1" />เพิ่ม</Button>
          </CardHeader>
          <CardContent>
            {formData.certificates.length === 0 ? (
              <p className="text-muted-foreground text-sm">ยังไม่มีใบรับรอง</p>
            ) : (
              <div className="space-y-3">
                {formData.certificates.map((c, i) => (
                  <div key={i} className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[150px]">
                      <Label className="text-xs">ใบรับรอง</Label>
                      <Select value={c.certificate_id || '__none__'} onValueChange={v => updateCertificate(i, 'certificate_id', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="เลือก..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- เลือก --</SelectItem>
                          {(filterOptions?.allCertificates || []).map(cert => (
                            <SelectItem key={cert.id} value={cert.id}>{cert.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">เกรด</Label>
                      <Input value={c.grade} onChange={e => updateCertificate(i, 'grade', e.target.value)} />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">สถานะ</Label>
                      <Input value={c.status} onChange={e => updateCertificate(i, 'status', e.target.value)} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Checkbox checked={c.certificate_received} onCheckedChange={v => updateCertificate(i, 'certificate_received', !!v)} />
                      <Label className="text-xs">ได้รับแล้ว</Label>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <Label className="text-xs">หมายเหตุ</Label>
                      <Input value={c.note} onChange={e => updateCertificate(i, 'note', e.target.value)} />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeCertificate(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section H: Student Proficiency */}
        <Card>
          <CardHeader><CardTitle>ความถนัดกลุ่มนักเรียน</CardTitle></CardHeader>
          <CardContent>{renderTagSection('studentProficiency')}</CardContent>
        </Card>

        {/* Bottom Save */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} size="lg">
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            บันทึกข้อมูล
          </Button>
        </div>
      </div>
    </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminTeacherEditPage;
